import { BaseButton } from '#src/base/button/base_button.tsx';
import { cn } from '#src/styling/cn.ts';
import { XIcon } from '@phosphor-icons/react';
import type { ComponentProps } from 'react';
import { Drawer as Primitive } from 'vaul';

export type DrawerRootProps = ComponentProps<typeof Primitive.Root>;

const DrawerRoot = ({ ...props }: DrawerRootProps) => {
  return <Primitive.Root data-slot="drawer" repositionInputs={false} {...props} />;
};

export type DrawerTriggerProps = ComponentProps<typeof Primitive.Trigger>;

const DrawerTrigger = (props: DrawerTriggerProps) => {
  return <Primitive.Trigger data-slot="drawer-trigger" {...props} />;
};

export type DrawerPortalProps = ComponentProps<typeof Primitive.Portal>;

const DrawerPortal = (props: DrawerPortalProps) => {
  return <Primitive.Portal data-slot="drawer-portal" {...props} />;
};

export type DrawerCloseProps = ComponentProps<typeof Primitive.Close>;

const DrawerClose = (props: DrawerCloseProps) => {
  return <Primitive.Close data-slot="drawer-close" {...props} />;
};

export type DrawerOverlayProps = ComponentProps<typeof Primitive.Overlay>;

const DrawerOverlay = ({ className, ...props }: DrawerOverlayProps) => {
  return (
    <Primitive.Overlay
      data-slot="drawer-overlay"
      className={cn('fixed inset-0 z-50 bg-black/50 backdrop-blur-sm', className)}
      {...props}
    />
  );
};

export type DrawerPopupProps = ComponentProps<'div'>;

const DrawerPopup = ({ className, ...props }: DrawerPopupProps) => {
  return (
    <div
      data-slot="drawer-popup"
      className={cn(
        'relative mt-24 max-h-[92dvh] w-full overflow-hidden rounded-t-4xl bg-white shadow-lg sm:max-w-xl',
        className,
      )}
      {...props}
    />
  );
};

export type DrawerContentProps = ComponentProps<typeof Primitive.Content> & {
  showCloseButton?: boolean;
  showHandle?: boolean;
  popupClassName?: string;
};

const DrawerContent = ({
  className,
  children,
  showCloseButton = true,
  showHandle = true,
  popupClassName,
  ...props
}: DrawerContentProps) => {
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <Primitive.Content
        data-slot="drawer-content-root"
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center outline-none"
        {...props}
      >
        <DrawerPopup className={popupClassName}>
          {showHandle && (
            <div
              data-slot="drawer-handle"
              className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-zinc-200"
            />
          )}
          <div data-slot="drawer-content" className={cn('min-h-0 px-6 pt-2 pb-6', className)}>
            {children}
          </div>
          {showCloseButton && (
            <BaseButton.Root
              asChild
              variant="ghost"
              size="icon-sm"
              className="absolute top-3 right-3"
            >
              <Primitive.Close data-slot="drawer-close">
                <BaseButton.Content variant="ghost" size="icon-sm">
                  <XIcon className="size-4" />
                  <span className="sr-only">Close</span>
                </BaseButton.Content>
              </Primitive.Close>
            </BaseButton.Root>
          )}
        </DrawerPopup>
      </Primitive.Content>
    </DrawerPortal>
  );
};

export type DrawerHeaderProps = ComponentProps<'div'>;

const DrawerHeader = ({ className, ...props }: DrawerHeaderProps) => {
  return (
    <div
      data-slot="drawer-header"
      className={cn('flex flex-col gap-2 text-left', className)}
      {...props}
    />
  );
};

export type DrawerFooterProps = ComponentProps<'div'>;

const DrawerFooter = ({ className, ...props }: DrawerFooterProps) => {
  return (
    <div
      data-slot="drawer-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
};

export type DrawerTitleProps = ComponentProps<typeof Primitive.Title>;

const DrawerTitle = ({ className, ...props }: DrawerTitleProps) => {
  return (
    <Primitive.Title
      data-slot="drawer-title"
      className={cn('text-lg leading-none font-bold', className)}
      {...props}
    />
  );
};

export type DrawerDescriptionProps = ComponentProps<typeof Primitive.Description>;

const DrawerDescription = ({ className, ...props }: DrawerDescriptionProps) => {
  return (
    <Primitive.Description
      data-slot="drawer-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
};

export const Drawer = {
  Root: DrawerRoot,
  Trigger: DrawerTrigger,
  Portal: DrawerPortal,
  Close: DrawerClose,
  Overlay: DrawerOverlay,
  Popup: DrawerPopup,
  Content: DrawerContent,
  Header: DrawerHeader,
  Footer: DrawerFooter,
  Title: DrawerTitle,
  Description: DrawerDescription,
};
